import { useState, useRef } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Button } from '@bancobarigui/barihaus';
import { PlusOutlined } from '@ant-design/icons';
import { Group } from '../types';
import { SectionItem } from '../SectionItem';

interface GroupContentProps {
  group: Group;
  onAddSection: (insertIndex?: number) => void;
  onEditSection: (sectionId: string, name: string) => void;
  onRemoveSection: (sectionId: string) => void;
  onDuplicateSection: (sectionId: string) => void;
  onAddQuestion: (sectionId: string, insertIndex?: number) => void;
  onEditQuestion: (sectionId: string, questionId: string, updates: any) => void;
  onRemoveQuestion: (sectionId: string, questionId: string) => void;
  onDuplicateQuestion: (sectionId: string, questionId: string) => void;
  allGroups: Group[];
  isDragging: boolean;
  draggedType: string | null;
}

export function GroupContent({
  group,
  onAddSection,
  onEditSection,
  onRemoveSection,
  onDuplicateSection,
  onAddQuestion,
  onEditQuestion,
  onRemoveQuestion,
  onDuplicateQuestion,
  allGroups,
  isDragging,
  draggedType,
}: GroupContentProps) {
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollIntervalRef = useRef<number | null>(null);

  const handleAutoScroll = (mouseY: number) => {
    const scrollThreshold = 100;
    const scrollSpeed = 10;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (mouseY < scrollThreshold) {
      scrollIntervalRef.current = window.setInterval(() => {
        window.scrollBy(0, -scrollSpeed);
      }, 16);
    } else if (mouseY > window.innerHeight - scrollThreshold) {
      scrollIntervalRef.current = window.setInterval(() => {
        window.scrollBy(0, scrollSpeed);
      }, 16);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedType !== 'new-section') {
      setDropIndex(null);
      return;
    }

    handleAutoScroll(e.clientY);

    if (group.sections.length === 0) {
      setDropIndex(0);
      return;
    }

    if (!containerRef.current) return;

    const mouseY = e.clientY;
    let targetIndex = group.sections.length;

    for (let i = 0; i < group.sections.length; i++) {
      const sectionEl = sectionRefs.current.get(i);
      if (sectionEl) {
        const rect = sectionEl.getBoundingClientRect();
        const middleY = rect.top + rect.height / 2;

        if (mouseY < middleY) {
          targetIndex = i;
          break;
        }
      }
    }

    setDropIndex(targetIndex);
  };

  const handleDragLeave = () => {
    setDropIndex(null);
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const controlType = e.dataTransfer.getData('text/plain');

    if (controlType === 'new-section' && dropIndex !== null) {
      onAddSection(dropIndex);
    }

    setDropIndex(null);

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  return (
    <>
      <Droppable
        droppableId={`sections-of-group-${group.id}`}
        type="section"
        isDropDisabled={isDragging}
      >
        {(provided) => (
          <div
            ref={(el) => {
              provided.innerRef(el);
              if (el && containerRef.current !== el) {
                (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
              }
            }}
            {...provided.droppableProps}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className="mt-4"
            style={{
              minHeight: group.sections.length === 0 ? '80px' : 'auto',
            }}
          >
            {group.sections.length === 0 && dropIndex === 0 && (
              <div
                className={`mt-3 mb-3 p-5 border-2 border-dashed rounded-lg transition-all ${
                  'border-green-400 bg-green-50'
                }`}
              >
                <p className="text-center text-sm mb-0 text-green-500">
                  Solte aqui para adicionar primeira seção
                </p>
              </div>
            )}
            {group.sections.map((section, sectionIndex) => (
              <div key={section.id}>
                {dropIndex === sectionIndex && (
                  <div
                    className={`mt-3 mb-3 p-5 border-2 border-dashed rounded-lg transition-all ${
                      'border-green-400 bg-green-50'
                    }`}
                  >
                    <p className="text-center text-sm mb-0 text-green-500">
                      Solte aqui para adicionar seção
                    </p>
                  </div>
                )}
                <div ref={(el) => el && sectionRefs.current.set(sectionIndex, el)}>
                  <SectionItem
                    section={section}
                    groupId={group.id}
                    index={sectionIndex}
                    onEdit={(sectionId, name) => onEditSection(sectionId, name)}
                    onRemove={(sectionId) => onRemoveSection(sectionId)}
                    onDuplicate={(sectionId) => onDuplicateSection(sectionId)}
                    onAddQuestion={(sectionId, insertIndex) => onAddQuestion(sectionId, insertIndex)}
                    onEditQuestion={(sectionId, questionId, updates) =>
                      onEditQuestion(sectionId, questionId, updates)
                    }
                    onRemoveQuestion={(sectionId, questionId) =>
                      onRemoveQuestion(sectionId, questionId)
                    }
                    onDuplicateQuestion={(sectionId, questionId) =>
                      onDuplicateQuestion(sectionId, questionId)
                    }
                    allGroups={allGroups}
                    draggedType={draggedType}
                  />
                </div>
              </div>
            ))}
            {dropIndex === group.sections.length && group.sections.length > 0 && (
              <div
                className={`mt-3 mb-3 p-5 border-2 border-dashed rounded-lg transition-all ${
                  'border-green-400 bg-green-50'
                }`}
              >
                <p className="text-center text-sm mb-0 text-green-500">
                  Solte aqui para adicionar seção
                </p>
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      <Button
        type="link"
        size="small"
        icon={<PlusOutlined />}
        onClick={() => onAddSection()}
        className="text-blue-600 font-medium p-0 mt-4"
      >
        Adicionar Seção
      </Button>
    </>
  );
}
